/**
 * Service Request Hub Page
 * 서비스 요청 허브 - 교민 생활서비스 요청
 */

import { ServiceRequestForm } from "@/components/business/ServiceRequestForm";
import { ServiceRequestList } from "@/components/business/ServiceRequestList";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { queryClient } from "@/lib/queryClient";
import { Sparkles, Truck, Utensils, Wrench, Zap } from "lucide-react";
import { useState } from "react";

const serviceCategories = [
  { id: "plumbing", label: "배관", icon: <Wrench className="w-5 h-5" /> },
  { id: "electrical", label: "전기", icon: <Zap className="w-5 h-5" /> },
  { id: "cleaning", label: "청소", icon: <Sparkles className="w-5 h-5" /> },
  { id: "moving", label: "이사", icon: <Truck className="w-5 h-5" /> },
  { id: "food", label: "음식", icon: <Utensils className="w-5 h-5" /> },
];

export default function ServiceRequestHub() {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  return (
    <PageContainer size="default">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">서비스 요청 허브</h1>
          <p className="text-muted-foreground">
            필요한 서비스를 요청하고 여러 업체의 견적을 받아보세요
          </p>
        </div>

        {/* Category Selection */}
        {!showForm && (
          <Card>
            <CardHeader>
              <CardTitle>어떤 서비스가 필요하신가요?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {serviceCategories.map((cat) => (
                  <Button
                    key={cat.id}
                    variant="outline"
                    className="h-24 flex flex-col items-center justify-center gap-2"
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setShowForm(true);
                    }}
                  >
                    {cat.icon}
                    <span>{cat.label}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Request Form */}
        {showForm && selectedCategory && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {
                    serviceCategories.find((c) => c.id === selectedCategory)
                      ?.label
                  }{" "}
                  서비스 요청
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedCategory("");
                  }}
                >
                  취소
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ServiceRequestForm
                category={selectedCategory as any}
                onSuccess={() => {
                  setShowForm(false);
                  setSelectedCategory("");
                  queryClient.invalidateQueries({
                    queryKey: ["/api/service-requests"],
                  });
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* My Requests */}
        {user && (
          <Card>
            <CardHeader>
              <CardTitle>내 요청</CardTitle>
            </CardHeader>
            <CardContent>
              <ServiceRequestList />
            </CardContent>
          </Card>
        )}
      </div>
    </PageContainer>
  );
}
